/**
 * Brand Share API Client (Story 40-8 — Epic 40, roll-up sharing).
 *
 * CLONED from `./dashboardShareApi.ts` and adapted for the BRAND roll-up model.
 * The ONE structural difference from the dashboard-share client: the agency-side
 * CRUD is keyed by `brandId` (a brand roll-up has NO single bound client) rather
 * than `clientId`, and the create body carries ONLY `dashboardId` (the server
 * derives `brand_id` from the dashboard — never the body).
 *
 *   - Public viewer fetchers (fetchBrandShareMeta / View / verifyPassword) hit
 *     `/api/brand-share-view/:token/*`. Same raw-fetch / no-Authorization /
 *     `credentials: "include"` / 10s-timeout contract as the dashboard-share
 *     viewer (which itself clones the report portal viewer).
 *   - Agency-side CRUD (create / list / revoke) hits `/api/brand-share-links`.
 *     `listBrandShareLinks` filters by `brandId`; `createBrandShareLink` sends
 *     `{ dashboardId, label, expiresAt, password?, hiddenMetrics?, hiddenIntegrations? }`.
 *
 * The public viewer fetchers reuse the SAME response types as the portal/dashboard
 * viewers (`PortalMeta` / `PortalViewResponse` / `PortalVerifyPasswordResponse`)
 * because the brand-share-view server routes return byte-identical shapes (the
 * server reports scope "full" and the `client` field carries the BRAND name), so
 * the existing PortalShell renders the roll-up canvas unchanged.
 */

import type {
  PortalMeta,
  PortalViewResponse,
  PortalMetaInactive,
  PortalVerifyPasswordResponse,
  PortalDateRangePreset,
  PortalComparisonPreset,
} from "@provenleap/types";
import { apiGet, apiPost, apiDelete } from "./apiClient";

// ============================================================================
// Public viewer — types (cloned from dashboardShareApi.ts)
// ============================================================================

export interface FetchBrandShareViewOptions {
  dateRange?: PortalDateRangePreset;
  comparison?: PortalComparisonPreset;
  /** When true, appends `?refresh=1` to bypass the server-side KV cache. */
  refresh?: boolean;
}

/** Result of a verify-password call — includes HTTP status for 429 handling. */
export interface VerifyBrandSharePasswordResult {
  status: number;
  body: PortalVerifyPasswordResponse | { error: string };
  /** When status === 429, parsed from the `Retry-After` header (in seconds). */
  retryAfter?: number;
}

/** Error shape surfaced to callers when fetch itself fails (network, timeout). */
export class BrandShareApiError extends Error {
  constructor(
    message: string,
    public readonly kind: "network" | "timeout" | "bad_response",
  ) {
    super(message);
    this.name = "BrandShareApiError";
  }
}

// ============================================================================
// Public viewer — constants
// ============================================================================

/** Fetch timeout (matches dashboardShareApi). 10s = the server per-platform budget. */
const BRAND_SHARE_FETCH_TIMEOUT_MS = 10_000;

// ============================================================================
// Public viewer — fetchers
// ============================================================================

/**
 * Fetch the meta bootstrap response for a brand share token.
 *
 * Returns the full `PortalMeta` union — callers switch on `status`:
 *   - `"inactive"` → render `<PortalInactivePage />`
 *   - `"active"` with `passwordProtected === true` → render password gate
 *   - `"active"` with `passwordProtected === false` → proceed to `/view`
 */
export async function fetchBrandShareMeta(token: string): Promise<PortalMeta> {
  const url = `/api/brand-share-view/${encodeURIComponent(token)}/meta`;
  const res = await safeFetch(url, { method: "GET", credentials: "include" });
  return parseJsonOrThrow<PortalMeta>(res);
}

/**
 * Submit a password for a brand share link.
 *
 * Returns `{ status, body, retryAfter? }` so callers can branch exactly like the
 * portal/dashboard password gate (200 verified / 200 not-verified / 429 rate-limited).
 */
export async function verifyBrandSharePassword(
  token: string,
  password: string,
): Promise<VerifyBrandSharePasswordResult> {
  const url = `/api/brand-share-view/${encodeURIComponent(token)}/verify-password`;
  const res = await safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });

  let body: PortalVerifyPasswordResponse | { error: string };
  try {
    body = (await res.json()) as PortalVerifyPasswordResponse | { error: string };
  } catch {
    throw new BrandShareApiError(
      "Invalid JSON response from verify-password",
      "bad_response",
    );
  }

  const retryAfterHeader = res.headers.get("Retry-After");
  const retryAfter = retryAfterHeader
    ? Number.parseInt(retryAfterHeader, 10)
    : undefined;

  return {
    status: res.status,
    body,
    ...(Number.isFinite(retryAfter) ? { retryAfter } : {}),
  };
}

/**
 * Fetch brand share view data.
 *
 * Returns a discriminated union mirroring the portal/dashboard viewer:
 *   - `PortalViewResponse` (status === "active") → render the shell
 *   - `PortalMetaInactive` (status === "inactive") → flip to inactive page
 *
 * A 401 `{ error: "password_required" }` is thrown as a
 * `BrandShareApiError("password_required", "bad_response")` so callers can
 * re-open the password gate.
 */
export async function fetchBrandShareView(
  token: string,
  options: FetchBrandShareViewOptions = {},
): Promise<PortalViewResponse | PortalMetaInactive> {
  const params = new URLSearchParams();
  if (options.dateRange) params.set("dateRange", options.dateRange);
  if (options.comparison) params.set("comparison", options.comparison);
  if (options.refresh) params.set("refresh", "1");

  const qs = params.toString();
  const url = `/api/brand-share-view/${encodeURIComponent(token)}/view${
    qs ? "?" + qs : ""
  }`;
  const res = await safeFetch(url, { method: "GET", credentials: "include" });

  if (res.status === 401) {
    throw new BrandShareApiError("password_required", "bad_response");
  }

  return parseJsonOrThrow<PortalViewResponse | PortalMetaInactive>(res);
}

// ============================================================================
// Public viewer — PortalShell / PortalPasswordGate backend seam
// ============================================================================

/**
 * Backend objects wiring the REUSED PortalShell / PortalPasswordGate components
 * to the brand-share-view endpoints instead of the report portal-view / dashboard
 * ones.
 *
 * Both components default to the report portal's `portalPublicApi`; the brand
 * public route (`brand-view.$token.tsx`) passes these via the `api` prop so
 * widgets load + passwords verify against `/api/brand-share-view/*`.
 *
 * The `isPasswordRequiredError` / `isTimeoutError` predicates match on
 * `BrandShareApiError` (this module's error class) so the shared components never
 * depend on another surface's error class for the brand surface.
 */
export const brandShareShellBackend = {
  fetchView: (token: string, options: FetchBrandShareViewOptions) =>
    fetchBrandShareView(token, options),
  /**
   * Brand roll-ups have no per-dashboard PDF — meta always reports
   * `scope: "full"` + `downloadable: false`, so PortalShell never renders the
   * download button and this is never invoked. Returning the (non-existent)
   * download path keeps the seam total without a special-case in the component.
   */
  downloadUrl: (token: string) =>
    `/api/brand-share-view/${encodeURIComponent(token)}/download`,
  isPasswordRequiredError: (error: unknown) =>
    error instanceof BrandShareApiError &&
    error.message === "password_required",
};

export const brandSharePasswordGateBackend = {
  verifyPassword: (token: string, password: string) =>
    verifyBrandSharePassword(token, password),
  isTimeoutError: (error: unknown) =>
    error instanceof BrandShareApiError && error.kind === "timeout",
};

// ============================================================================
// Public viewer — internal helpers
// ============================================================================

async function safeFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, {
      ...init,
      signal: AbortSignal.timeout(BRAND_SHARE_FETCH_TIMEOUT_MS),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new BrandShareApiError("Request timed out", "timeout");
    }
    throw new BrandShareApiError(
      err instanceof Error ? err.message : "Network error",
      "network",
    );
  }
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    throw new BrandShareApiError("Invalid JSON response", "bad_response");
  }
}

// ============================================================================
// Agency-side CRUD — types (cloned from dashboardShareApi.ts, brand-keyed)
// ============================================================================

export type BrandShareLinkStatus = "active" | "expired" | "revoked";
export type BrandShareExpirationPreset = "7d" | "30d" | "90d" | "never";

/** Mirror of the server `BrandShareLinkProjection` shape (brand-keyed, no clientId). */
export interface BrandShareLinkProjection {
  id: string;
  brandId: string;
  dashboardId: string;
  label: string;
  tokenPrefix: string;
  hiddenMetricsJson: string | null;
  hiddenIntegrationsJson: string | null;
  expiresAt: number | null;
  passwordProtected: boolean;
  createdBy: string | null;
  revokedAt: number | null;
  lastAccessedAt: number | null;
  accessCount: number;
  createdAt: number;
  updatedAt: number;
  status: BrandShareLinkStatus;
}

export interface BrandShareLinkWithToken extends BrandShareLinkProjection {
  /** Raw token — surfaced exactly once on create/regenerate. Treat as a secret. */
  token: string;
  /** Full constructed share URL ready to copy and share. */
  url: string;
}

/** Create input — carries `dashboardId` directly; brand_id is SERVER-DERIVED. */
export interface CreateBrandShareLinkInput {
  dashboardId: string;
  label: string;
  expiresAt: BrandShareExpirationPreset;
  password?: string;
  hiddenMetrics?: string[];
  hiddenIntegrations?: string[];
}

interface ApiError {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  errors?: Array<{ field: string; message: string }>;
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    const message = err.detail ?? err.title ?? `Request failed (${res.status})`;
    const error = new Error(message) as Error & {
      status?: number;
      apiError?: ApiError;
    };
    error.status = res.status;
    error.apiError = err;
    throw error;
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

// ============================================================================
// Agency-side CRUD — fetchers (brand-keyed)
// ============================================================================

export async function listBrandShareLinks(
  brandId: string,
): Promise<BrandShareLinkProjection[]> {
  const res = await apiGet(
    `/api/brand-share-links?brandId=${encodeURIComponent(brandId)}`,
  );
  return unwrap<BrandShareLinkProjection[]>(res);
}

export async function createBrandShareLink(
  input: CreateBrandShareLinkInput,
): Promise<BrandShareLinkWithToken> {
  const res = await apiPost(`/api/brand-share-links`, input);
  return unwrap<BrandShareLinkWithToken>(res);
}

export async function revokeBrandShareLink(id: string): Promise<void> {
  const res = await apiDelete(`/api/brand-share-links/${id}`);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    throw new Error(err.detail ?? err.title ?? "Failed to revoke link");
  }
}

export async function regenerateBrandShareLinkToken(
  id: string,
): Promise<BrandShareLinkWithToken> {
  const res = await apiPost(`/api/brand-share-links/${id}/regenerate-token`, {});
  return unwrap<BrandShareLinkWithToken>(res);
}

// ============================================================================
// Agency-side CRUD — formatting helpers (cloned from dashboardShareApi.ts)
// ============================================================================

/**
 * Format a relative date string for "expires in X days" UX. Falls back to an
 * absolute date when the diff is more than 90 days for clarity.
 */
export function formatBrandShareExpiration(
  expiresAt: number | null,
  now: number = Date.now(),
): string {
  if (expiresAt === null) return "Never expires";
  const diff = expiresAt - now;
  if (diff <= 0) return "Expired";
  const days = Math.round(diff / 86_400_000);
  if (days <= 1) return "Expires today";
  if (days <= 30) return `Expires in ${days} days`;
  if (days <= 90) return `Expires in ${Math.round(days / 7)} weeks`;
  return `Expires ${new Date(expiresAt).toLocaleDateString()}`;
}
